/**
 * Custom element that truncates rich text at a configurable character limit
 * and renders expand/collapse buttons inline at the end of the text.
 */
class TextTruncate extends HTMLElement {
  /** @type {boolean} */
  #expanded = false;

  /** @type {HTMLElement | null} */
  #truncatedEl = null;

  /** @type {HTMLElement | null} */
  #contentEl = null;

  connectedCallback() {
    const limit = parseInt(this.dataset.limit || '200', 10);
    const showMoreLabel = this.dataset.showMore || 'Show more';
    const showLessLabel = this.dataset.showLess || 'Show less';

    this.#init(limit, showMoreLabel, showLessLabel);
  }

  /**
   * @param {number} limit
   * @param {string} showMoreLabel
   * @param {string} showLessLabel
   */
  #init(limit, showMoreLabel, showLessLabel) {
    const contentEl = /** @type {HTMLElement | null} */ (this.firstElementChild);
    if (!contentEl || limit <= 0) return;

    const plainText = contentEl.textContent?.trim() ?? '';
    if (plainText.length <= limit) return;

    this.#contentEl = contentEl;

    // Clone the content element to create the truncated preview
    const truncatedEl = /** @type {HTMLElement} */ (contentEl.cloneNode(true));
    truncatedEl.setAttribute('aria-hidden', 'true');
    this.#truncateElement(truncatedEl, limit);
    this.#truncatedEl = truncatedEl;

    // Insert "Show more" inline at the end of the truncated content
    const showMoreBtn = this.#createButton(showMoreLabel, 'false');
    showMoreBtn.addEventListener('click', this.#toggle);
    this.#findLastTextParent(truncatedEl).appendChild(showMoreBtn);

    // Insert "Show less" inline at the end of the full content
    const showLessBtn = this.#createButton(showLessLabel, 'true');
    showLessBtn.addEventListener('click', this.#toggle);
    this.#findLastTextParent(contentEl).appendChild(showLessBtn);

    contentEl.before(truncatedEl);
    contentEl.setAttribute('hidden', '');
  }

  /**
   * @param {string} label
   * @param {string} ariaExpanded
   * @returns {HTMLButtonElement}
   */
  #createButton(label, ariaExpanded) {
    const btn = /** @type {HTMLButtonElement} */ (document.createElement('button'));
    btn.type = 'button';
    btn.className = 'text-truncate__toggle';
    btn.textContent = label;
    btn.setAttribute('aria-expanded', ariaExpanded);
    return btn;
  }

  #toggle = () => {
    this.#expanded = !this.#expanded;

    if (this.#expanded) {
      this.#truncatedEl?.setAttribute('hidden', '');
      this.#truncatedEl?.removeAttribute('aria-hidden');
      this.#contentEl?.removeAttribute('hidden');
    } else {
      this.#truncatedEl?.removeAttribute('hidden');
      this.#truncatedEl?.setAttribute('aria-hidden', 'true');
      this.#contentEl?.setAttribute('hidden', '');
    }
  };

  /**
   * Returns the parent element of the last non-empty text node in the tree,
   * used as the inline insertion point for the toggle button.
   *
   * @param {HTMLElement} element
   * @returns {HTMLElement}
   */
  #findLastTextParent(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let lastTextNode = null;
    let node;
    while ((node = walker.nextNode())) {
      if (node.textContent?.trim()) lastTextNode = node;
    }
    return /** @type {HTMLElement} */ (lastTextNode?.parentElement ?? element);
  }

  /**
   * Walks the cloned element's text nodes and truncates at the character limit,
   * appending an ellipsis. Removes any nodes that fall entirely after the limit.
   *
   * @param {Node} element
   * @param {number} limit
   */
  #truncateElement(element, limit) {
    let count = 0;

    const processNode = (/** @type {Node} */ node) => {
      if (count >= limit) {
        node.parentNode?.removeChild(node);
        return;
      }

      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        const remaining = limit - count;
        if (text.length > remaining) {
          node.textContent = text.slice(0, remaining).trimEnd() + '\u2026';
          count = limit;
        } else {
          count += text.length;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const children = [...node.childNodes];
        for (const child of children) {
          processNode(child);
        }
      }
    };

    processNode(element);
  }
}

if (!customElements.get('text-truncate')) {
  customElements.define('text-truncate', TextTruncate);
}
